import type { CarDetails } from '@policyvision/contracts';

import {
  Car,
  CarGroupSection,
  CarRow,
  PolicyExtractedData,
  Section,
} from '@/policy/types/PolicyCoverage';

function flattenCars(
  policy: PolicyExtractedData,
  carGroupTitle: string,
): CarGroupSection | null {
  const carSituations =
    policy.extractedData.situations?.filter((s) => s.type === 'car') ?? [];

  if (carSituations.length === 0) return null;

  const cars: Car[] = carSituations.map((situation) => {
    const details = situation.details as CarDetails;
    const sections: CarRow[] = situation.warranties.flatMap((warranty) => {
      const rows: CarRow[] = [
        {
          identifier: `warranty-${warranty.uuid}`,
          warranties: warranty.name,
          coverageAmount: warranty.amount,
          deductible: warranty.deductible,
          premium: warranty.premium,
        },
      ];
      warranty.sections?.forEach((sub) => {
        rows.push({
          identifier: `warranty-${warranty.uuid}-section-${sub.uuid}`,
          warranties: sub.name,
          coverageAmount: sub.amount,
          deductible: sub.deductible,
          premium: sub.premium,
        });
      });
      return rows;
    });
    return { ...details, sections };
  });

  const allOverlays = carSituations.flatMap((s) => s.overlayRectangles ?? []);

  return {
    kind: 'car',
    identifier: 'auto-cars-group',
    title: carGroupTitle,
    overlayRectangles: allOverlays,
    cars,
  };
}

export function flattenSections(
  policy: PolicyExtractedData,
  carGroupTitle: string,
): Section[] {
  const { extractionType, extractedData } = policy;
  const result: Section[] = [];

  if (extractionType === 'AUTO') {
    const carGroup = flattenCars(policy, carGroupTitle);
    if (carGroup) result.push(carGroup);
  } else {
    for (const situation of extractedData.situations ?? []) {
      result.push({
        ...situation,
        kind: 'warranty',
        identifier: situation.uuid,
      });
    }
  }

  for (const a of extractedData.amendments) result.push(a);
  if (extractedData.policy) result.push(extractedData.policy);
  if (extractedData.assignment) result.push(extractedData.assignment);
  if (extractedData.financialData) result.push(extractedData.financialData);

  return result
    .filter((section) => section.overlayRectangles?.[0] != null)
    .sort((a, b) => {
      const pageDiff =
        a.overlayRectangles[0]?.pageNumber - b.overlayRectangles[0]?.pageNumber;
      if (pageDiff !== 0) return pageDiff;
      return a.overlayRectangles[0]?.y - b.overlayRectangles[0]?.y;
    });
}
